import { analyzeCorpus, type CorpusStats, type TuneFacts } from '@/music/corpus';
import { parseIRealURL } from '@/music/ireal/parse';
import type { IRealChart } from '@/music/ireal/types';
import { tuneIdentity } from '@/music/tuneRelations';
import { BUNDLED_PLAYLISTS, fetchBundledPlaylist } from './bundledPlaylists';

export interface CorpusRepository {
  charts: IRealChart[];
  stats: CorpusStats;
  findTune: (title: string, composer?: string) => TuneFacts | null;
}

export function createCorpusRepository(charts: IRealChart[]): CorpusRepository {
  const stats = analyzeCorpus(charts);
  const byIdentity = new Map<string, TuneFacts>();
  const byTitle = new Map<string, TuneFacts>();
  for (const tune of stats.facts) {
    const key = tuneIdentity(tune.title, tune.composer);
    if (!byIdentity.has(key)) byIdentity.set(key, tune);
    const titleKey = tuneIdentity(tune.title);
    if (!byTitle.has(titleKey)) byTitle.set(titleKey, tune);
  }

  return {
    charts,
    stats,
    findTune: (title, composer) =>
      (composer
        ? byIdentity.get(tuneIdentity(title, composer))
        : byTitle.get(tuneIdentity(title))) ?? null,
  };
}

let cachedRepository: CorpusRepository | null = null;
let pendingRepository: Promise<CorpusRepository> | null = null;

export function getCachedCorpusRepository(): CorpusRepository | null {
  return cachedRepository;
}

export function loadCorpusRepository(): Promise<CorpusRepository> {
  if (cachedRepository) return Promise.resolve(cachedRepository);
  if (pendingRepository) return pendingRepository;

  pendingRepository = (async () => {
    const raw = await fetchBundledPlaylist(BUNDLED_PLAYLISTS[0]);
    const { songs } = parseIRealURL(raw);
    cachedRepository = createCorpusRepository(songs);
    return cachedRepository;
  })().finally(() => {
    pendingRepository = null;
  });

  return pendingRepository;
}
